import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";
import axios from "axios";
import SalesReportTable from "./SalesReportTable";
import TransactionsReportTable from "./TransactionsReportTable";


const ReportPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const currentRole = getCookie("_r");
    const adminRole = process.env.REACT_APP_ADMIN_ROLE;
    const financeRole = process.env.REACT_APP_FINANCE_ROLE;
    const clientRole = process.env.REACT_APP_CLIENT_ROLE;
    const rentalPartnerRole = process.env.REACT_APP_PARTNER_ROLE;
    if (currentRole !== adminRole && currentRole !== financeRole && currentRole !== clientRole && currentRole !== rentalPartnerRole) {
      navigate("/", { replace: true });
    }
  }, []);

  const [userData, setUserData] = useState(null);
  const [allBranches, setAllBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = getCookie("_AT");
        if (!token) return;
        
        const response = await axios.get(`${config.apiUrl}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserData(response.data);
      } catch (error) {
        console.error("Error fetching user data", error);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoadingBranches(true);
        const token = getCookie("_AT");
        if (!token) {
          setLoadingBranches(false);
          return;
        }

        const response = await axios.get(`${config.apiUrl}/api/branches/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: -1 } // Fetch all branches
        });

        const branches = response.data.data.map((branch) => ({
          code: branch.code,
          name: branch.Name,
        }));
        setAllBranches(branches);
      } catch (error) {
        console.error("Error fetching branches", error);
        setAllBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, []);

  // Compute available branches based on role/data
  const getAvailableBranches = () => {
    if (loadingBranches || !userData) {
      return []; // Return empty while loading to avoid showing stale data
    }

    const userRole = userData.role?.id;
    const adminRole = process.env.REACT_APP_ADMIN_ROLE;
    const financeRole = process.env.REACT_APP_FINANCE_ROLE;
    
    // Admin and Finance can see all
    if (userRole === adminRole || userRole === financeRole) {
      return allBranches;
    }

    // For Clients and Rental Partners (or others), restrict to their assigned branches
    if (userData.branches && userData.branches.length > 0) {
      const userBranchCodes = userData.branches.map(b => b.branch_id?.code);
      return allBranches.filter(branch => userBranchCodes.includes(branch.code));
    }

    // If no branches assigned but restricted role, return empty or default? 
    // Safest is to show nothing or let them select nothing.
    return [];
  };

  const availableBranches = getAvailableBranches();

  // Show "All" option only when user has access to multiple branches
  const showAllBranchesOption = availableBranches.length > 1;

  const [formData, setFormData] = useState({
    branch: "",
    startDate: "",
    endDate: "",
    reportType: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [popUp, setPopUp] = useState({
    title: "",
    description: "",
    isError: false,
    showPopUp: false,
    hideCancel: false,
  });

  const callPopUp = (props) => {
    setPopUp({ ...popUp, ...props });
    if (props.showPopUp) {
      setTimeout(() => {
        setPopUp({ ...popUp, showPopUp: false });
      }, 3000);
    }
  };

  // Format date for datetime-local input
  const formatDateForInput = (date) => {
    const tzOffset = new Date().getTimezoneOffset() * 60000;
    return new Date(date - tzOffset).toISOString().slice(0, 16);
  };

  // Set date presets
  const setPreset = (startDate, endDate) => {
    setFormData((prev) => ({
      ...prev,
      startDate: formatDateForInput(startDate),
      endDate: formatDateForInput(endDate),
    }));
  };

  // Preset date handlers
  const handlePresetClick = (preset) => {
    const now = new Date();
    const todayStart = new Date(now.setHours(0, 0, 0, 0));
    const todayEnd = new Date();
    const yesterdayStart = new Date(todayStart - 24 * 60 * 60 * 1000);
    const yesterdayEnd = new Date(todayStart - 1000);
    const last7DaysStart = new Date(todayStart - 7 * 24 * 60 * 60 * 1000);
    const last30DaysStart = new Date(todayStart - 30 * 24 * 60 * 60 * 1000);

    switch (preset) {
      case "today":
        setPreset(todayStart, todayEnd);
        break;
      case "yesterday":
        setPreset(yesterdayStart, yesterdayEnd);
        break;
      case "last7Days":
        setPreset(last7DaysStart, yesterdayEnd);
        break;
      case "last30Days":
        setPreset(last30DaysStart, yesterdayEnd);
        break;
      default:
        break;
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Helper function to fetch report for a single branch
  const fetchBranchReport = async (branchCode, actionUrl, startDate, endDate) => {
    const submitData = {
      branch: branchCode,
      startDate: startDate,
      endDate: endDate,
    };

    const response = await fetch(`${config.apiUrl}${actionUrl}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getCookie("_AT")}`,
      },
      body: JSON.stringify(submitData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed for branch ${branchCode}: ${response.status} - ${errorData.error || response.statusText}`);
    }

    const responseData = await response.json();
    return responseData;
  };

  // Merge multiple branch reports into a single unified report
  const mergeReports = (reports, branchCodes) => {
    const allRows = [];
    let totalReservations = new Set();

    reports.forEach((report, index) => {
      if (report.success && report.data && report.data.rows) {
        // Add branch info to each row for reference
        report.data.rows.forEach(row => {
          allRows.push(row);
          if (row.reservationId) {
            totalReservations.add(`${branchCodes[index]}-${row.reservationId}`);
          }
        });
      }
    });

    // Sort merged rows by reservation ID (desc) then entry date (desc)
    allRows.sort((a, b) => {
      const ida = Number.isFinite(Number(a.reservationId)) ? Number(a.reservationId) : -Infinity;
      const idb = Number.isFinite(Number(b.reservationId)) ? Number(b.reservationId) : -Infinity;
      if (ida !== idb) {
        return idb - ida;
      }
      // Parse entry dates (DD/MM/YYYY format)
      const parseDate = (dateStr) => {
        if (!dateStr) return 0;
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
        }
        return new Date(dateStr).getTime();
      };
      return parseDate(b.entryDate) - parseDate(a.entryDate);
    });

    return {
      summary: {
        totalRows: allRows.length,
        totalReservations: totalReservations.size,
        dateRange: reports[0]?.data?.summary?.dateRange || {},
        branch: "ALL",
        branchesIncluded: branchCodes,
      },
      rows: allRows,
    };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.branch ||
      !formData.startDate ||
      !formData.endDate ||
      !formData.reportType
    ) {
      alert("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Determine the action URL based on report type
      let actionUrl = "/api/reports/default";

      if (formData.reportType === "sales") {
        actionUrl = "/api/reports/sales";
      } else if (formData.reportType === "transactions") {
        actionUrl = "/api/reports/transactions";
      }

      // Handle "ALL" branches selection
      if (formData.branch === "ALL") {
        console.log("Fetching reports for all branches:", availableBranches.map(b => b.code));
        
        // Fetch reports for all branches in parallel
        const branchCodes = availableBranches.map(b => b.code);
        const reportPromises = branchCodes.map(branchCode => 
          fetchBranchReport(branchCode, actionUrl, formData.startDate, formData.endDate)
            .catch(error => {
              console.warn(`Warning: Failed to fetch report for branch ${branchCode}:`, error.message);
              return { success: false, error: error.message, branchCode };
            })
        );

        const reports = await Promise.all(reportPromises);
        
        // Check for any failures
        const failures = reports.filter(r => !r.success);
        if (failures.length === reports.length) {
          throw new Error("Failed to fetch reports for all branches");
        }
        
        if (failures.length > 0) {
          const failedBranches = failures.map(f => f.branchCode).join(", ");
          console.warn(`Partial success: Failed to fetch data for branches: ${failedBranches}`);
        }

        // Merge successful reports
        const successfulReports = reports.filter(r => r.success);
        const successfulBranchCodes = branchCodes.filter((_, i) => reports[i].success);
        const mergedData = mergeReports(successfulReports, successfulBranchCodes);

        console.log("Merged Report Data:", mergedData);

        if (mergedData.rows.length > 0) {
          setReportData(mergedData);
          setShowTable(true);
        } else {
          alert("Report generated but no data found for any branch.");
        }
      } else {
        // Single branch request (existing logic)
        const submitData = {
          branch: formData.branch,
          startDate: formData.startDate,
          endDate: formData.endDate,
        };

        console.log("Sending request to:", `${config.apiUrl}${actionUrl}`);
        console.log("Request data:", submitData);

        const response = await fetch(`${config.apiUrl}${actionUrl}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getCookie("_AT")}`,
          },
          body: JSON.stringify(submitData),
        });

        if (response.ok) {
          const responseData = await response.json();
          console.log("Report Response:", responseData);

          if (responseData.success && responseData.data) {
            setReportData(responseData.data);
            setShowTable(true);
          } else {
            alert("Report generated but no data found.");
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error("Error response:", errorData);
          throw new Error(
            `Failed to generate report: ${response.status} ${response.statusText}`
          );
        }
      }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Error generating report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle back button click
  const handleBack = () => {
    setShowTable(false);
    setReportData(null);
  };

  // If showing table, render the appropriate table component
  if (showTable && reportData) {
    const tableProps = {
      reportData: reportData,
      onBack: handleBack,
      reportType: formData.reportType,
      dateRange: {
        startDate: formData.startDate,
        endDate: formData.endDate,
      },
      branch: formData.branch,
    };

    // Render the appropriate table based on report type
    if (formData.reportType === "transactions") {
      return <TransactionsReportTable {...tableProps} />;
    } else {
      return <SalesReportTable {...tableProps} />;
    }
  }

  return (
    <div className="flex flex-col items-center justify-start bg-gray-100 p-6 min-h-screen">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
          <h1 className="text-2xl font-bold mb-6 text-gray-800 text-center">
            Generate Reports
          </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Branch Selector */}
          <div>
            <label
              htmlFor="branch"
              className="block text-gray-700 font-medium mb-2"
            >
              Branch
            </label>
            <select
              name="branch"
              id="branch"
              value={formData.branch}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer"
            >
              <option value="" disabled>Select branch</option>
              {showAllBranchesOption && (
                <option value="ALL">All Branches</option>
              )}
              {availableBranches.map((option) => (
                <option key={option.code} value={option.code}>{option.name}</option>
              ))}
            </select>
          </div>

          {/* Preset Buttons */}
          <div>
            <p className="text-sm text-gray-600 mb-3 font-medium">
              Quick Presets:
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handlePresetClick("today")}
                className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => handlePresetClick("yesterday")}
                className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
              >
                Yesterday
              </button>
              <button
                type="button"
                onClick={() => handlePresetClick("last7Days")}
                className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
              >
                Last 7 Days
              </button>
              <button
                type="button"
                onClick={() => handlePresetClick("last30Days")}
                className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
              >
                Last 30 Days
              </button>
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label
              htmlFor="startDate"
              className="block text-gray-700 font-medium mb-2"
            >
              Start Date & Time
            </label>
            <input
              type="datetime-local"
              name="startDate"
              id="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer"
            />
          </div>

          {/* End Date */}
          <div>
            <label
              htmlFor="endDate"
              className="block text-gray-700 font-medium mb-2"
            >
              End Date & Time
            </label>
            <input
              type="datetime-local"
              name="endDate"
              id="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer"
            />
          </div>

          {/* Report Type */}
          <div>
            <label
              htmlFor="reportType"
              className="block text-gray-700 font-medium mb-2"
            >
              Report Type
            </label>
            <select
              name="reportType"
              id="reportType"
              value={formData.reportType}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer"
            >
              <option value="" disabled>
                Select report type
              </option>
              <option value="sales">Sales Report</option>
              <option value="transactions">Transactions Report</option>
            </select>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Generating Report...
              </span>
            ) : (
              "Generate Report"
            )}
          </button>
        </form>
      </div>


      {/* Popup Notification (Simplistic) */}
      {popUp.showPopUp && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded shadow-lg text-white ${popUp.isError ? "bg-red-500" : "bg-green-500"}`}>
            <p className="font-bold">{popUp.title}</p>
            <p>{popUp.description}</p>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
